import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";

export const WorkspaceSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	name: z.string().min(1),
	status: z.enum(["ACTIVE", "ARCHIVED"]),
	createdAt: IsoDateTimeSchema,
});
export type Workspace = z.infer<typeof WorkspaceSchema>;

/**
 * Design §21.9 — `ownerUserId` is copied from the AgentSession owner at
 * creation time and is never auto-updated. `authenticationProfileRef` is
 * provenance only and confers NO ownership: an artifact produced with a system
 * credential still belongs to the session owner.
 */
export const ArtifactSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema,
	workspaceId: IdSchema,
	ownerUserId: IdSchema,
	generatedInSessionId: IdSchema.nullable(),
	generatedByExecutionId: IdSchema.nullable(),
	generatedByNodeExecutionId: IdSchema.nullable(),
	generatedByActorType: z.enum(["USER", "AGENT", "SYSTEM", "PLATFORM_ADMIN"]),
	generatedByActorId: IdSchema,
	authenticationProfileRef: IdSchema.nullable(),
	name: z.string().min(1),
	type: z.string().min(1),
	mimeType: z.string().min(1),
	version: z.number().int().min(1),
	/** Lineage across versions. v1 points at itself. */
	lineageId: IdSchema,
	/** Immutable pointer into the object store. */
	storageRef: z.string().min(1),
	sizeBytes: z.number().int().min(0),
	/** sha256 hex of the stored bytes. */
	checksum: z.string().min(1),
	checksumAlgorithm: z.literal("sha256"),
	status: z.enum(["DRAFT", "COMMITTED", "SUPERSEDED", "DELETED"]),
	retentionUntil: IsoDateTimeSchema.nullable(),
	createdAt: IsoDateTimeSchema,
});
export type Artifact = z.infer<typeof ArtifactSchema>;

export const CreateArtifactRequestSchema = z.object({
	projectId: IdSchema,
	workspaceId: IdSchema.nullable().default(null),
	sessionId: IdSchema.nullable().default(null),
	executionId: IdSchema.nullable().default(null),
	nodeExecutionId: IdSchema.nullable().default(null),
	ownerUserId: IdSchema,
	generatedByActorType: z.enum(["USER", "AGENT", "SYSTEM", "PLATFORM_ADMIN"]).default("AGENT"),
	generatedByActorId: IdSchema,
	authenticationProfileRef: IdSchema.nullable().default(null),
	name: z.string().min(1).max(200),
	type: z.string().min(1).max(60),
	mimeType: z.string().min(1).max(120).default("text/markdown"),
	content: z.string(),
	/** Supersede an existing artifact instead of creating v1. */
	supersedesArtifactId: IdSchema.nullable().default(null),
});
export type CreateArtifactRequest = z.infer<typeof CreateArtifactRequestSchema>;

export const ArtifactContentSchema = z.object({
	artifactId: IdSchema,
	name: z.string(),
	mimeType: z.string(),
	version: z.number().int().min(1),
	sizeBytes: z.number().int().min(0),
	checksum: z.string(),
	/** Recomputed from the stored bytes on every read. */
	checksumVerified: z.boolean(),
	content: z.string(),
});
export type ArtifactContent = z.infer<typeof ArtifactContentSchema>;

export const StorageObjectSchema = z.object({
	storageRef: z.string(),
	backend: z.enum(["local-fs", "r2", "s3"]),
	bucket: z.string(),
	key: z.string(),
	sizeBytes: z.number().int().min(0),
	checksum: z.string(),
	createdAt: IsoDateTimeSchema,
});
export type StorageObject = z.infer<typeof StorageObjectSchema>;

export const OwnershipTransferSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	resourceType: z.enum(["ARTIFACT", "SESSION"]),
	resourceId: IdSchema,
	fromOwnerUserId: IdSchema,
	toOwnerUserId: IdSchema,
	reason: z.string(),
	requestedBy: IdSchema,
	approvedBy: IdSchema.nullable(),
	transferredAt: IsoDateTimeSchema,
});
export type OwnershipTransfer = z.infer<typeof OwnershipTransferSchema>;

export const TransferOwnershipRequestSchema = z.object({
	toOwnerUserId: IdSchema,
	reason: z.string().min(1).max(500),
});
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequestSchema>;

export const ArtifactPageSchema = pageSchema(ArtifactSchema);
export const WorkspacePageSchema = pageSchema(WorkspaceSchema);
export const OwnershipTransferPageSchema = pageSchema(OwnershipTransferSchema);
export const StorageObjectPageSchema = pageSchema(StorageObjectSchema);
